import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { v4 as uuidv4 } from "uuid";

import { useEditingContext } from "components/BudgetBuilder/shared/EditingContext";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Subsection from "components/Subsection";
import Table from "components/Table";
import {
	saveBudgetBuilderForm,
	updateExternalCostShareLineItems,
} from "store/actions/budgetBuilder";
import { budgetBuilderSelectors } from "store/selectors";
import { getCostShareSubtotal } from "utils/budgetBuilder";

import ExternalCostShareButtons from "./ExternalCostShareButtons";
import ExternalCostShareRow from "./ExternalCostShareRow";
import styles from "./styles.css";

const ExternalCostShareList = () => {
	const { externalCostShare: [ isEditing, setIsEditing ] } = useEditingContext();
	const [ isModified, setIsModified ] = useState(false);
	const savedCategories = useSelector(budgetBuilderSelectors.externalCostShare);
	const { currencyFormatter } = useTenantFormatter();
	const [ editedCategories, setEditedCategories ] = useState([]);
	const handleSetCategories = updatedCategories => {
		setEditedCategories(updatedCategories);
		setIsModified(true);
	};

	useEffect(() => {
		setEditedCategories(savedCategories);
	}, [ savedCategories ]);

	const handleSave = () => {
		setIsEditing(false);
		updateExternalCostShareLineItems(editedCategories);
		saveBudgetBuilderForm();
	};

	const costShareSubtotalString = useMemo(
		() => currencyFormatter(getCostShareSubtotal(editedCategories)),
		[ currencyFormatter, editedCategories ]
	);

	const handleRemoveItem = hash => {
		const filterFunction = member => member.hash !== hash;
		const updatedCategories = [ ...editedCategories.filter(filterFunction) ];
		handleSetCategories(updatedCategories);
	};

	const addExternalCostShareItem = () => {
		const newItem = {
			hash: uuidv4(),
			value: "",
			requirement: "",
			externalOrg: "",
			type: "",
			costShareAmount: 0.00,
		};
		let updatedCategories = [ ...editedCategories ];
		updatedCategories.push(newItem);
		handleSetCategories(updatedCategories);
		setIsEditing(true);
	};

	const handleReset = () => {
		setEditedCategories(savedCategories);
		setIsModified(false);
	};

	const handleUpdateExternalLineItem = (hash, update) => {
		let newCategories = [ ...editedCategories ];
		for (let i = 0; i < newCategories.length; i++) {
			let category = newCategories[i];
			if (category.hash === hash) {
				newCategories[i] = {
					...category,
					...update,
				};
				break;
			}
		}
		handleSetCategories(newCategories);
	};

	const renderTableRow = category => (
		<ExternalCostShareRow
			key={category.hash}
			handleUpdateExternalLineItem={handleUpdateExternalLineItem}
			category={category}
			isEditing={isEditing}
			handleRemoveItem={handleRemoveItem}
		/>
	);

	const renderCategories = () => {
		return editedCategories?.sort((a, b) => a.sort - b.sort).map((category, index) => (
			<Fragment key={index}>
				{renderTableRow(category, index)}
				{category.items &&
				category.items
					.sort((a, b) => a.sort - b.sort)
					.map((item, childIndex) => renderTableRow(item, index, childIndex))}
			</Fragment>
		));
	};

	return (
		<Subsection
			title={"External Cost Share"}
			buttons={ExternalCostShareButtons({
				isEditing, setIsEditing, isModified, handleSave, handleReset, addExternalCostShareItem
			})}
		>

			<Table captionText="External Cost Share">
				<Table.Header>
					<Table.Row>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Line Item"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Requirement"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"External Org"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignLeft}>
							{"Type"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignRight}>
							{"Cost Share Amount"}
						</Table.HeaderCell>
						<Table.HeaderCell className={styles.cellAlignCenter}>
						</Table.HeaderCell>
					</Table.Row>
				</Table.Header>
				<Table.Body>
					{renderCategories()}
					<Table.Row className={`${styles.rowHeader} ${styles.row} ${styles.totalRowPadding}`}>
						<Table.Cell className={`${styles.rowHeaderCell} ${styles.totalCellPadding}`}>
							<span className={styles.bold}>{"External Subtotal"}</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell className={styles.rowHeaderCell}/>
						<Table.Cell
							className={`${styles.rowHeaderCell} ${styles.cellAlignRight} ${styles.valueCellPadding}`}
						>
							<span className={`${styles.bold} ${styles.cellAlignRight}`}>
								{costShareSubtotalString}
							</span>
						</Table.Cell>
						<Table.Cell className={styles.rowHeaderCell}>
						</Table.Cell>
					</Table.Row>
				</Table.Body>
			</Table>
		</Subsection>
	);
};

export default ExternalCostShareList;
